import { Link, Navigate } from 'react-router';
import PageShell from '@components/pages/PageShell';
import { ArticleCard, SectionLabel, type ArticleItem } from '@components/pages/ResourceSections';
import { onImageError } from '@/lib/imageFallback';
import Newsletter from '@components/landing/Newsletter';

/** Paragraphs shown before the pull quote; the remainder follows it. */
const SPLIT_AFTER = 3;

const SHARE = [
    { name: 'Facebook', icon: '/landing/blog/facebook.svg' },
    { name: 'X', icon: '/landing/blog/twitter.svg' },
    { name: 'Instagram', icon: '/landing/blog/instagram.svg' },
    { name: 'YouTube', icon: '/landing/blog/youtube.svg' },
];

/**
 * The long-form reading layout behind both the blog and the stories section:
 * headline, byline, hero image, body split by a pull quote, and a related rail.
 */
export default function ArticleDetail({
    item,
    related,
    basePath,
    backLabel,
    relatedLabel,
    kindLabel = 'article',
    meta,
}: {
    item?: ArticleItem;
    related: ArticleItem[];
    basePath: string;
    backLabel: string;
    relatedLabel: string;
    /** Singular noun used in the share prompt — "article" or "story". */
    kindLabel?: string;
    /** Overrides the date · reading-time line; the CMS values are used when it is omitted. */
    meta?: string;
}) {
    if (!item) return <Navigate to="/404" replace />;

    const body = item.body ?? [];
    const head = body.slice(0, SPLIT_AFTER);
    const tail = body.slice(SPLIT_AFTER);
    const metaLine =
        meta ?? [item.date, item.readingMinutes ? `${item.readingMinutes} min read` : ''].filter(Boolean).join(' · ');

    return (
        <PageShell>
            <article className="bg-white pb-14 pt-[120px] lg:pb-20 lg:pt-[160px]">
                <div className="mx-auto max-w-[1640px] px-5 lg:px-[140px]">
                    <Link to={basePath} className="text-[13px] font-semibold text-[#5e5e5e] hover:text-[#f60]">
                        &larr; {backLabel}
                    </Link>

                    <h1 className="font-primary mt-5 max-w-[900px] text-[32px] font-bold leading-[1.2] tracking-[-1px] text-[#08080c] lg:text-[52px] lg:tracking-[-2px]">
                        {item.title}
                    </h1>

                    <div className="mt-6 flex flex-wrap items-center gap-4">
                        <span className="flex items-center gap-2.5">
                            <img src={item.avatar} alt="" className="size-9 rounded-full object-cover" />
                            <span className="text-[13px] font-semibold text-[#08080c]">By {item.author}</span>
                        </span>
                        {metaLine && <span className="text-[13px] text-[#5e5e5e]">{metaLine}</span>}
                    </div>

                    <div className="mt-8 h-[220px] w-full overflow-hidden rounded-[14px] bg-[#d9d9d9] lg:h-[520px]">
                        <img
                            src={item.image}
                            alt=""
                            onError={onImageError(item.fallbackImage)}
                            className="size-full object-cover"
                        />
                    </div>

                    <div className="mt-10 flex flex-col gap-10 lg:flex-row lg:gap-16">
                        <div className="min-w-0 lg:flex-1">
                            {item.bodyHtml ? (
                                // Sanitised by the API on every write, against the same allow-list the
                                // editor uses — headings, lists, quotes, links and images only.
                                <div className="article-body" dangerouslySetInnerHTML={{ __html: item.bodyHtml }} />
                            ) : (
                                <>
                                    {head.map((p, i) => (
                                        <p
                                            key={i}
                                            className="mb-5 text-[14px] leading-[1.9] text-[#333] lg:text-[15px]"
                                        >
                                            {p}
                                        </p>
                                    ))}
                                    {item.quote && (
                                        <blockquote className="my-8 border-l-[3px] border-[#f60] pl-6 text-[18px] font-medium leading-[1.6] text-[#08080c] lg:text-[22px]">
                                            {item.quote}
                                        </blockquote>
                                    )}
                                    {tail.map((p, i) => (
                                        <p
                                            key={`b-${i}`}
                                            className="mb-5 text-[14px] leading-[1.9] text-[#333] lg:text-[15px]"
                                        >
                                            {p}
                                        </p>
                                    ))}
                                </>
                            )}

                            {(item.tags?.length || item.originalUrl) && (
                                <footer className="mt-10 flex flex-wrap items-center gap-2 border-t border-[#e0e0e0] pt-6">
                                    {item.tags?.map((t) => (
                                        <span
                                            key={t}
                                            className="rounded-full bg-[#f2f2f2] px-3 py-1 text-[12px] font-medium text-[#333]"
                                        >
                                            {t}
                                        </span>
                                    ))}
                                    {item.originalUrl && (
                                        <a
                                            href={item.originalUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="ml-auto text-[12px] font-semibold text-[#5e5e5e] hover:text-[#f60]"
                                        >
                                            Originally published on Medium ↗
                                        </a>
                                    )}
                                </footer>
                            )}
                        </div>

                        <aside className="lg:w-[240px] lg:shrink-0">
                            <p className="text-[13px] font-bold text-[#08080c]">Share this {kindLabel}</p>
                            <div className="mt-4 flex gap-3">
                                {SHARE.map((s) => (
                                    <a
                                        key={s.name}
                                        href="#"
                                        aria-label={`Share on ${s.name}`}
                                        className="flex size-9 items-center justify-center rounded-full border border-[#e0e0e0]"
                                    >
                                        <img src={s.icon} alt="" className="size-4" />
                                    </a>
                                ))}
                            </div>
                        </aside>
                    </div>
                </div>
            </article>

            <section className="bg-white pb-14 lg:pb-20">
                <div className="mx-auto max-w-[1640px] px-5 lg:px-[140px]">
                    <SectionLabel>{relatedLabel}</SectionLabel>
                    <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                        {related.map((a) => (
                            <ArticleCard key={a.slug} item={a} basePath={basePath} />
                        ))}
                    </div>
                </div>
            </section>

            <Newsletter />
        </PageShell>
    );
}
